/**
 * §8.2 Resource Categories — 10 large interactive tile-cards linking
 * directly to the deeper sections on this page.
 */

import React from 'react'
import clsx from 'clsx'
import {
  BookOpen, Map, GraduationCap, PlayCircle, HelpCircle, Terminal,
  GitCommit, MessageCircleQuestion, LifeBuoy, Users, ArrowRight, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { CATEGORIES } from '../data'
import { TONE } from '../_shared'

const ICONS = {
  BookOpen, Map, GraduationCap, PlayCircle, HelpCircle, Terminal,
  GitCommit, MessageCircleQuestion, LifeBuoy, Users, Sparkles,
}

export default function Categories() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Resource Categories</Eyebrow>
          <SectionHeading align="center"
            title={<>Ten places to find{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">what you&apos;re looking for.</span></>}
            subtitle="From product documentation to release notes and the future community — every knowledge type has a home."
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
          {CATEGORIES.map((c) => {
            const tone = TONE[c.tone] || TONE.orange
            const Icon = ICONS[c.iconKey] || Sparkles
            const soon = /coming/i.test(c.count)
            return (
              <a key={c.id} href={c.href}
                className={clsx(
                  'group relative rounded-2xl border p-5 backdrop-blur-xl overflow-hidden transition-all',
                  'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[2px]',
                )}
              >
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${tone.rgb},0.14), transparent 70%)` }} />
                <div className="relative flex items-start justify-between mb-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <span className={clsx(
                    'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9.5px] font-mono uppercase tracking-wider',
                    soon
                      ? 'border-amber-400/30 bg-amber-400/10 text-amber-400'
                      : 'border-emerald-400/25 bg-emerald-400/[0.06] text-emerald-400',
                  )}>{c.count}</span>
                </div>
                <div className="relative">
                  <div className="text-[15px] font-semibold text-white leading-tight">{c.label}</div>
                  <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-1.5 min-h-[38px]">{c.body}</p>
                </div>
                <div className="relative mt-3 inline-flex items-center gap-1 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 group-hover:text-brand-gold transition-colors">
                  Open <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </a>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
